import React, { useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import i18next from 'i18next';
import { ChevronRight, ChevronDown, LogOut, Globe, Check } from 'lucide-react';

import { account } from '@redux/account';
import { useDispatch } from 'utils/useDispatch';
import { ProfileAvatar } from 'components/ProfileAvatar/ProfileAvatar';
import { SidebarDropdown } from './SidebarDropdown';

const languages = [
  { code: 'en', title: 'English' },
  { code: 'ru', title: 'Русский' },
  { code: 'uk', title: 'Українська' },
  { code: 'de', title: 'Deutsch' },
  { code: 'es', title: 'Español' },
  { code: 'fr', title: 'Français' },
  { code: 'it', title: 'Italiano' },
  { code: 'nl', title: 'Nederlands' },
  { code: 'tr', title: 'Türkçe' },
  { code: 'pl', title: 'Polski' },
  { code: 'pt', title: 'Português' }
];

export const ProfileDropdown = ({ isCollapsed }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector(account.selectors.user);
  const displayName = useSelector(account.selectors.displayName);
  const currentLanguage = useSelector(account.selectors.language);
  const setLanguage = useDispatch(account.actions.setLanguage);
  const [langHovered, setLangHovered] = useState(false);
  const [langExpanded, setLangExpanded] = useState(false); // For mobile
  const hoverTimeoutRef = useRef(null);

  const currentLangName = languages.find(l => l.code === currentLanguage)?.title || currentLanguage;

  const handleLangChange = useCallback((lng, close) => {
    setLanguage(lng);
    i18next.changeLanguage(lng);
    setLangExpanded(false);
    close();
  }, [setLanguage]);

  const handleLogout = useCallback(() => {
    navigate('/auth/logout');
  }, [navigate]);

  // Handle hover with delay to allow moving to submenu
  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setLangHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setLangHovered(false);
    }, 150); // Small delay to allow moving to submenu
  };

  const emailAt = user?.email?.lastIndexOf('@');

  const trigger = (
    <button
      className={classNames(
        'group flex flex-col w-full mt-2 rounded-field cursor-pointer text-left',
        'transition-all duration-200 ease-out',
        'hover:bg-ui-overlay',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ui-accent-ring',
        isCollapsed ? 'p-2' : 'p-2.5'
      )}
    >
      <span className={classNames('flex w-full items-center', { 'justify-center': isCollapsed })}>
        <ProfileAvatar
          name={user?.name || user?.email}
          size='sm'
          className='transition-transform duration-200 ease-out group-hover:scale-105'
        />
        {!isCollapsed && (
          <>
            <span className="text-ui text-sm font-medium truncate ml-2.5 min-w-0 flex-1">
              {displayName}
            </span>
            <ChevronRight
              size={16}
              className="flex-none ml-1 text-ui-4 transition-transform duration-200 ease-out group-hover:text-ui"
            />
          </>
        )}
      </span>
      {/* On its own line, so the full address fits the narrow sidebar. A longer one wraps,
          preferably right before the @, and is never cut off. */}
      {!isCollapsed && user?.email && (
        <span className="mt-1.5 w-full break-words text-xs text-ui-4">
          {user.email.slice(0, emailAt)}<wbr />{user.email.slice(emailAt)}
        </span>
      )}
    </button>
  );

  return (
    <SidebarDropdown trigger={trigger}>
      {({ close, isMobile }) => (
        <div className="py-2">
          {/* User info header */}
          <div className="px-4 py-3 border-b border-ui-hair-3 mb-2">
            <div className="flex items-center">
              <ProfileAvatar name={user?.name || user?.email} size='md' />
              <div className="ml-3 min-w-0">
                <div className="text-ui text-sm font-medium truncate">{displayName}</div>
                <div className="text-ui-3 text-xs truncate">{user?.email}</div>
              </div>
            </div>
          </div>

          {/* Language - different behavior for mobile vs desktop */}
          {isMobile ? (
            // Mobile: expandable accordion
            <div className="px-2">
              <button
                onClick={() => setLangExpanded(!langExpanded)}
                className={classNames(
                  'flex items-center justify-between w-full px-3 py-2.5 rounded-tile text-sm',
                  'transition-colors duration-150',
                  'text-ui-2 hover:bg-ui-overlay hover:text-ui'
                )}
              >
                <span className="flex items-center">
                  <Globe size={16} className="mr-3 text-ui-3" />
                  {currentLangName}
                </span>
                <ChevronDown
                  size={14}
                  className={classNames(
                    'text-ui-4 transition-transform duration-200',
                    { 'rotate-180': langExpanded }
                  )}
                />
              </button>
              {langExpanded && (
                <div className="mt-1 ml-4 border-l border-ui-hair-3 pl-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang.code, close)}
                      className={classNames(
                        'flex items-center justify-between w-full px-3 py-2 rounded text-sm text-left',
                        'transition-colors duration-150',
                        lang.code === currentLanguage
                          ? 'text-ui-accent'
                          : 'text-ui-3 hover:bg-ui-overlay hover:text-ui'
                      )}
                    >
                      {lang.title}
                      {lang.code === currentLanguage && <Check size={14} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Desktop: hover submenu
            <div
              className="relative px-2"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={classNames(
                  'flex items-center justify-between w-full px-3 py-2.5 rounded-tile text-sm',
                  'transition-colors duration-150',
                  langHovered
                    ? 'bg-ui-overlay text-ui'
                    : 'text-ui-2 hover:bg-ui-overlay hover:text-ui'
                )}
              >
                <span className="flex items-center">
                  <Globe size={16} className="mr-3 text-ui-3" />
                  {currentLangName}
                </span>
                <ChevronRight size={14} className="text-ui-4" />
              </button>

              {/* Language submenu - positioned to right, aligned to bottom of button */}
              <div
                className={classNames(
                  'absolute left-full bottom-0 ml-1 min-w-40',
                  'bg-ui-elevated border border-ui-hair-3 rounded-field shadow-menu',
                  'transition-opacity duration-150',
                  {
                    'opacity-100 pointer-events-auto': langHovered,
                    'opacity-0 pointer-events-none': !langHovered
                  }
                )}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div className="py-2 max-h-64 overflow-y-auto">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang.code, close)}
                      className={classNames(
                        'flex items-center justify-between w-full px-4 py-2 text-sm text-left',
                        'transition-colors duration-150',
                        lang.code === currentLanguage
                          ? 'text-ui-accent bg-ui-accent-wash'
                          : 'text-ui-2 hover:bg-ui-overlay hover:text-ui'
                      )}
                    >
                      {lang.title}
                      {lang.code === currentLanguage && <Check size={14} />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Logout */}
          <div className="border-t border-ui-hair-3 mt-2 pt-2 px-2">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2.5 rounded-tile text-ui-danger hover:bg-ui-danger-wash transition-colors duration-150"
            >
              <LogOut size={16} className="mr-3" />
              {t('auth.logout')}
            </button>
          </div>
        </div>
      )}
    </SidebarDropdown>
  );
};
